import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Crown, Phone, ShieldCheck, BadgeCheck, Calendar,
  User, GraduationCap, MapPin, Heart, Users,
  X, Camera, ChevronLeft, ChevronRight, Info, Pencil,
} from "lucide-react";
import SettingsLayout from "./SettingsLayout";
import useSettingsProfile from "./useSettingsProfile";
import { PROFILE_VERIFICATION_STATUSES } from "../../utils/verification";
import { isPremiumActive, formatMembershipDate, parseMembershipDate } from "../../utils/membership";
import { getProfileDisplayImage } from "../../utils/profilePlaceholder";

const statusConfig = {
  [PROFILE_VERIFICATION_STATUSES.VERIFIED]: { color: "bg-emerald-50 text-emerald-700", label: "Verified" },
  [PROFILE_VERIFICATION_STATUSES.PENDING]: { color: "bg-amber-50 text-amber-700", label: "Pending" },
  [PROFILE_VERIFICATION_STATUSES.REJECTED]: { color: "bg-rose-50 text-rose-700", label: "Rejected" },
  [PROFILE_VERIFICATION_STATUSES.CHANGES_REQUESTED]: { color: "bg-orange-50 text-orange-700", label: "Action Required" },
  [PROFILE_VERIFICATION_STATUSES.DRAFT]: { color: "bg-sand-100 text-ink-500", label: "Draft" },
};

function formatDate(date) {
  if (!date) return "—";
  const d = date.toDate ? date.toDate() : new Date(date);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/* Tooltip/popover that works on both hover (desktop) and tap (mobile) */
function RemarksPopover({ remarks }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useState(null);

  if (!remarks) return null;

  const items = remarks.split(/\n|•|-/).map((s) => s.trim()).filter(Boolean);

  const openPopover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({
      top: rect.top + window.scrollY,
      right: window.innerWidth - rect.right,
    });
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        type="button"
        onMouseEnter={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setPos({ top: rect.top + window.scrollY, right: window.innerWidth - rect.right });
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onClick={openPopover}
        className="ml-1 flex h-5 w-5 items-center justify-center rounded-full text-orange-400 transition hover:text-orange-600"
        aria-label="View requested changes"
      >
        <Info size={14} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Full-screen tap-to-close on mobile */}
            <div
              className="fixed inset-0 z-[90] sm:hidden"
              onClick={() => setOpen(false)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 4 }}
              transition={{ duration: 0.15 }}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
              style={{
                position: "fixed",
                top: pos.top - 8,
                right: pos.right,
                transform: "translateY(-100%)",
                zIndex: 9999,
              }}
              className="w-64 overflow-hidden rounded-2xl border border-orange-200 bg-white shadow-2xl"
            >
              <div className="flex items-center gap-2 border-b border-orange-100 bg-orange-50 px-3.5 py-2.5">
                <Info size={12} className="shrink-0 text-orange-500" />
                <p className="text-[11.5px] font-bold text-orange-700">Action Required</p>
              </div>
              <div className="px-3.5 py-3 space-y-2">
                {items.length > 1 ? (
                  items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#c90243] text-[8px] font-bold text-white">
                        {idx + 1}
                      </span>
                      <span className="text-[12px] leading-5 text-ink-700">{item}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-[12px] leading-5 text-ink-700">{remarks}</p>
                )}
              </div>
              <div className="border-t border-orange-100 px-3.5 py-2.5">
                <Link
                  to="/my-profile"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-1.5 rounded-xl bg-[#c90243] py-2 text-[12px] font-semibold text-white transition hover:bg-[#a8003a]"
                >
                  Update profile →
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default function AccountSettings() {
  const { user, profile, loading, error } = useSettingsProfile();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const phone = profile?.mobileNumber || profile?.phone || user?.phoneNumber || "—";
  const verificationStatus = profile?.profileStatus || PROFILE_VERIFICATION_STATUSES.DRAFT;
  const premium = isPremiumActive(profile);
  const expiresAt = parseMembershipDate(profile?.membershipExpiresAt);
  const statusInfo = statusConfig[verificationStatus] || statusConfig[PROFILE_VERIFICATION_STATUSES.DRAFT];
  const photos = (profile?.imageUrls || []).filter(Boolean);

  return (
    <SettingsLayout title="My Account" description="Your complete profile details.">
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-brand-600" size={28} />
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-5">
          <section className="relative overflow-hidden rounded-[26px] border border-brand-100 bg-gradient-to-br from-white via-white to-brand-50/70 p-4 shadow-soft sm:rounded-3xl sm:p-5">
            <div className="pointer-events-none absolute -right-12 -top-16 h-36 w-36 rounded-full bg-brand-100/60 blur-3xl" />
            <div className="relative flex min-w-0 items-center gap-3.5">
              <button
                type="button"
                onClick={() => photos.length > 0 && setLightboxIndex(0)}
                className="h-[68px] w-[68px] shrink-0 overflow-hidden rounded-2xl border-2 border-white shadow-soft focus:outline-none focus:ring-2 focus:ring-brand-500"
                aria-label={photos.length ? "View profile photos" : "Profile photo"}
              >
                <img
                  src={getProfileDisplayImage(profile)}
                  alt={profile?.fullName || "Profile"}
                  className="h-full w-full object-cover object-center"
                />
              </button>
              <div className="min-w-0 flex-1">
                <h2 className="truncate font-display text-base font-bold text-ink-800 sm:text-lg">
                  {profile?.fullName || "My profile"}
                </h2>
                <p className="mt-0.5 truncate text-[12px] font-medium text-ink-500">
                  {profile?.kmProfileId || "Profile ID not available"}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold ${statusInfo.color}`}>
                    <ShieldCheck size={11} /> {statusInfo.label}
                  </span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold ${
                    premium ? "bg-amber-100 text-amber-800" : "bg-sand-100 text-ink-600"
                  }`}>
                    <Crown size={11} /> {premium ? "Premium" : "Free"}
                  </span>
                </div>
              </div>
              <Link
                to="/my-profile"
                aria-label="Edit profile"
                title="Edit profile"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sand-200 bg-white text-ink-600 transition hover:border-brand-300 hover:text-brand-600"
              >
                <Pencil size={15} />
              </Link>
            </div>
          </section>

          {/* Account Info */}
          <Section title="Account" Icon={ShieldCheck}>
            <Row label="Profile ID" value={profile?.kmProfileId || "—"} />
            <Row label="Phone" value={phone} />
            <Row
              label="Verification"
              value={statusInfo.label}
              badge={statusInfo.color}
              action={
                verificationStatus === PROFILE_VERIFICATION_STATUSES.CHANGES_REQUESTED
                  ? <RemarksPopover remarks={profile?.verificationRemarks} />
                  : verificationStatus === PROFILE_VERIFICATION_STATUSES.REJECTED
                  ? <RemarksPopover remarks={profile?.verificationRemarks} />
                  : null
              }
            />
            <Row wide label="Membership" value={premium ? `Premium${expiresAt ? ` · until ${formatMembershipDate(expiresAt)}` : ""}` : "Free"} action={!premium && <Link to="/settings/membership" className="rounded-lg bg-brand-gradient px-2.5 py-1 text-[11px] font-bold text-white">Upgrade</Link>} />
            <Row label="Member since" value={formatDate(profile?.createdAt)} />
          </Section>

          {/* Photos — at top after account info */}
          {photos.length > 0 && (
            <Section title={`Photos (${photos.length})`} Icon={Camera}>
              <div className="grid grid-cols-3 gap-2 py-3">
                {photos.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    className="relative w-full aspect-square overflow-hidden rounded-xl border border-sand-200 focus:outline-none focus:ring-2 focus:ring-brand-600"
                  >
                    <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover object-center" />
                  </button>
                ))}
              </div>
            </Section>
          )}

          {/* Personal */}
          <Section title="Personal" Icon={User}>
            <Row label="Full name" value={profile?.fullName} />
            <Row label="Gender" value={profile?.gender} />
            <Row label="Date of birth" value={formatDate(profile?.dateOfBirth)} />
            <Row label="Time of birth" value={profile?.timeOfBirth} />
            <Row label="Marital status" value={profile?.maritalStatus} />
            <Row label="Height" value={profile?.height} />
            <Row label="Religion" value={profile?.religion} />
            <Row label="Caste" value={profile?.caste} />
            <Row label="Gotram" value={profile?.gotram} />
            <Row label="Rashi" value={profile?.rashi} />
            <Row label="Nakshatram" value={profile?.nakshatram} />
            <Row label="Birth place" value={profile?.birthPlace} />
            <Row wide label="About me" value={profile?.aboutMe} />
          </Section>

          {/* Education & Career */}
          <Section title="Education & Career" Icon={GraduationCap}>
            <Row label="Education" value={profile?.education} />
            <Row label="College type" value={profile?.collegeType} />
            <Row wide label="College name" value={profile?.collegeName} />
            <Row label="Profession" value={profile?.profession} />
            <Row label="Company" value={profile?.companyName} />
            <Row label="Working place" value={profile?.workingPlace} />
            <Row label="Annual income" value={profile?.annualIncome} />
            <Row wide label="Other info" value={profile?.otherInformation} />
          </Section>

          {/* Family */}
          <Section title="Family" Icon={Users}>
            <Row label="Father name" value={profile?.fatherName} />
            <Row label="Father profession" value={profile?.fatherProfession} />
            <Row label="Father designation" value={profile?.fatherDesignation} />
            <Row label="Mother name" value={profile?.motherName} />
            <Row label="Mother surname" value={profile?.motherSurname} />
            <Row label="Mother profession" value={profile?.motherProfession} />
            <Row label="Brothers" value={profile?.brothers} />
            <Row label="Married brothers" value={profile?.marriedBrothers} />
            <Row label="Sisters" value={profile?.sisters} />
            <Row label="Married sisters" value={profile?.marriedSisters} />
            <Row label="Family status" value={profile?.familyFinancialStatus} />
          </Section>

          {/* Address */}
          <Section title="Address" Icon={MapPin}>
            <Row label="Residence type" value={profile?.residenceType} />
            <Row label="Country" value={profile?.addressCountry} />
            <Row label="State" value={profile?.state} />
            <Row label="District" value={profile?.district} />
            <Row label="City / Town" value={profile?.cityTown} />
            <Row label="Area" value={profile?.area} />
            <Row label="Pincode" value={profile?.pincode} />
            <Row label="Native place" value={profile?.nativePlace} />
            <Row wide label="Present residence" value={profile?.presentResidence} />
            <Row label="Visa status" value={profile?.visaStatus} />
          </Section>

          {/* Partner Preferences */}
          <Section title="Partner Preferences" Icon={Heart}>
            <Row label="Age range" value={profile?.preferredBirthYearFrom && profile?.preferredBirthYearTo ? `${profile.preferredBirthYearFrom} – ${profile.preferredBirthYearTo}` : null} />
            <Row label="Height" value={profile?.preferredHeight} />
            <Row label="Education" value={profile?.preferredEducation} />
            <Row label="Profession" value={profile?.preferredProfession} />
            <Row label="Caste" value={profile?.castePreference} />
            <Row label="Religion" value={profile?.preferredReligion} />
            <Row label="Marital status" value={profile?.preferredMaritalStatus} />
            <Row label="Residence type" value={profile?.preferredResidenceType} />
            <Row label="Location" value={profile?.preferredLocation} />
            <Row wide label="Other expectations" value={profile?.otherExpectations} />
          </Section>

          {/* Contact */}
          <Section title="Contact" Icon={Phone}>
            <Row label="Mobile" value={profile?.mobileNumber} />
            <Row label="Alternate" value={profile?.alternateNumber} />
          </Section>

          {error && (
            <p className="rounded-2xl bg-rose-50 px-4 py-3 text-[13px] text-rose-700">{error}</p>
          )}
        </div>
      )}

      {/* Photo Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && photos[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
            onClick={() => setLightboxIndex(null)}
            onTouchStart={(e) => { e.currentTarget._startX = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const diff = e.changedTouches[0].clientX - (e.currentTarget._startX || 0);
              if (diff < -50 && lightboxIndex < photos.length - 1) setLightboxIndex(lightboxIndex + 1);
              else if (diff > 50 && lightboxIndex > 0) setLightboxIndex(lightboxIndex - 1);
            }}
          >
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              className="absolute right-4 top-4 z-10 h-10 w-10 flex items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
            >
              <X size={18} />
            </button>

            <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[12px] font-semibold text-white backdrop-blur">
              <Camera size={13} /> {lightboxIndex + 1} / {photos.length}
            </div>

            {photos.length > 1 && lightboxIndex > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
                className="absolute left-4 top-1/2 z-10 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            {photos.length > 1 && lightboxIndex < photos.length - 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
              >
                <ChevronRight size={20} />
              </button>
            )}

            <img
              key={lightboxIndex}
              src={photos[lightboxIndex]}
              alt={`Photo ${lightboxIndex + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            />

            {/* Dots */}
            {photos.length > 1 && (
              <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-1.5">
                {photos.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      idx === lightboxIndex ? "w-5 bg-white" : "w-2 bg-white/40"
                    }`}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </SettingsLayout>
  );
}

function Section({ title, Icon, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-sm sm:rounded-3xl">
      <div className="flex items-center gap-3 border-b border-sand-100 px-4 py-3.5 sm:px-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <Icon size={16} />
        </div>
        <h3 className="font-display text-[14px] font-bold text-ink-800">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-2.5 p-3 sm:gap-3 sm:p-4">{children}</div>
    </section>
  );
}

function Row({ label, value, badge, action, wide = false }) {
  if (!value || value === "—" || value === "0") return null;
  return (
    <div className={`min-w-0 rounded-xl bg-sand-50/80 px-3 py-2.5 ${wide ? "col-span-2" : ""}`}>
      <span className="block truncate text-[10.5px] font-medium leading-4 text-ink-400 sm:text-[11px]">{label}</span>
      <div className="mt-1 flex min-w-0 items-start gap-1.5">
        {badge ? (
          <span className={`inline-flex max-w-full rounded-full px-2 py-0.5 text-[10px] font-bold ${badge}`}>{value}</span>
        ) : (
          <span className="min-w-0 break-words text-[12px] font-semibold leading-4 text-ink-800 sm:text-[13px]">{value}</span>
        )}
        {action && action}
      </div>
    </div>
  );
}
